// <notify-prompt> — luxury pop-up asking to allow browser notifications.
// Shows after 12 seconds or once the shopper scrolls 40% of the page, only when the browser supports
// notifications and the shopper hasn't decided yet. "Not now" hides it for 7 days. In the theme editor
// (data-preview) it shows once per page load and never asks the browser (the preview frame blocks it).
// Commands: --allow, --dismiss (buttons use commandfor="notify-prompt").
class NotifyPrompt extends HTMLElement {
  static KEY = 'aq_notify_prompt_until';

  connectedCallback() {
    this.preview = this.hasAttribute('data-preview');
    this.addEventListener('command', (event) => {
      if (event.command === '--allow') this.#allow();
      if (event.command === '--dismiss') this.#dismiss();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#dismiss();
    });
    if (!this.preview && !this.#eligible()) return;
    this.#schedule(this.preview ? 1500 : 12000);
  }

  #eligible() {
    if (!('Notification' in window) || Notification.permission !== 'default') return false;
    try {
      const until = Number(localStorage.getItem(NotifyPrompt.KEY) || 0);
      if (until > Date.now()) return false;
    } catch { /* storage blocked: still allowed */ }
    return true;
  }

  #schedule(delay) {
    let done = false;
    const show = () => {
      if (done) return;
      done = true;
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
      this.#open();
    };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max > 0.4) show();
    };
    const timer = setTimeout(show, delay);
    if (!this.preview) window.addEventListener('scroll', onScroll, { passive: true });
  }

  #open() {
    this.querySelector('[data-np-body]').hidden = false;
    this.querySelector('[data-np-thanks]').hidden = true;
    this.querySelector('[data-np-blocked]').hidden = true;
    this.querySelector('[data-np-actions]').hidden = false;
    this.removeAttribute('data-done');
    this.removeAttribute('data-closing');
    this.hidden = false;
    this.toggleAttribute('data-open', true);
  }

  #close() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.toggleAttribute('data-closing', true);
    setTimeout(() => {
      this.hidden = true;
      this.removeAttribute('data-open');
      this.removeAttribute('data-closing');
    }, reduce ? 0 : 350);
  }

  #dismiss() {
    if (!this.preview) {
      try { localStorage.setItem(NotifyPrompt.KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000)); } catch { /* ignore */ }
    }
    this.#close();
  }

  // "Allow" subscribes through Uppush: its hidden opt-in button is clicked in the same tap, so Uppush
  // asks the browser and registers this device right away. If Uppush's opt-in isn't on the page yet,
  // the browser is asked directly and Uppush subscribes the device automatically on the next page view.
  // In the theme editor the browser always blocks permission requests inside the preview frame, so
  // the editor only shows the success state as a demo and never asks.
  async #allow() {
    if (this.busy) return;
    if (this.preview) return this.#showState('thanks');
    this.busy = true;
    const allowButton = this.querySelector('.np-allow');
    if (allowButton) allowButton.disabled = true;
    let result = 'default';
    try {
      const uppushAllow = document.querySelector('#uppush-notification .up-popup-primary-button');
      if (uppushAllow) {
        uppushAllow.click();
        result = await this.#waitForPermission();
      } else if ('Notification' in window && Notification.permission === 'default') {
        result = await Notification.requestPermission();
      } else if ('Notification' in window) {
        result = Notification.permission;
      }
    } catch { result = 'default'; }
    this.busy = false;
    if (allowButton) allowButton.disabled = false;
    if (result === 'granted') return this.#showState('thanks');
    if (result === 'denied') {
      this.#snooze();
      return this.#showState('blocked');
    }
    // Prompt closed without a choice: don't ask again for a week.
    this.#dismiss();
  }

  #showState(state) {
    this.querySelector('[data-np-body]').hidden = true;
    this.querySelector('[data-np-thanks]').hidden = state !== 'thanks';
    this.querySelector('[data-np-blocked]').hidden = state !== 'blocked';
    this.querySelector('[data-np-actions]').hidden = true;
    this.toggleAttribute('data-done', state === 'thanks');
    setTimeout(() => this.#close(), state === 'thanks' ? 2600 : 5000);
  }

  #snooze() {
    if (this.preview) return;
    try { localStorage.setItem(NotifyPrompt.KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000)); } catch { /* ignore */ }
  }

  #waitForPermission() {
    return new Promise((resolve) => {
      const started = Date.now();
      const check = () => {
        const state = Notification.permission;
        if (state !== 'default' || Date.now() - started > 60000) return resolve(state);
        setTimeout(check, 400);
      };
      setTimeout(check, 400);
    });
  }
}

if (!customElements.get('notify-prompt')) customElements.define('notify-prompt', NotifyPrompt);
